import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  amenitiesApi,
  parseVacuumOrigin,
  type AmenityBlock,
  type AmenityDeviceOut,
  type VacuumOrigin
} from '@/api/amenities';
import { chargingApi } from '@/api/charging';
import { walletApi, walletNumbers, type WalletOut } from '@/api/wallet';
import { Card } from '@/components/ui/Card';
import { Display, Eyebrow } from '@/components/ui/Typography';
import { StatusPill } from '@/components/ui/StatusPill';
import { ScanQrOverlay } from '@/components/qr/ScanQrOverlay';
import { formatBRL } from '@/lib/format';
import { friendlyMessage } from '@/lib/errors';
import { useDevHardwareToggle } from '@/hooks/useDevHardwareToggle';

/**
 * O QR escaneado aponta para o MESMO aspirador desta tela?
 *
 * Compara só o caminho, ignorando a querystring: desde a atribuição de origem
 * (2026-08-23) o parser carimba `?origin=direct` no retorno do scanner, e a
 * comparação estrita passou a reprovar o QR correto — quebrando o início de
 * QUALQUER aspiração, já que escanear o adesivo do equipamento é obrigatório.
 * A origem é telemetria; não faz parte da identidade do aspirador.
 */
export function isSameVacuumTarget(scanned: string, expected: string): boolean {
  return scanned.split('?')[0] === expected.split('?')[0];
}

/** Contagem da fase de preparo antes de acionar o relé (T9c′). */
const PREP_SECONDS = 15;

type PayFrom = 'cash' | 'bonus';

/**
 * O 2º scan do adesivo pode ser dispensado?
 *
 * O scan obrigatório existe pra não acionar o relé sem o cliente estar na
 * frente do equipamento (feedback Saulo 2026-07-08). Quem chega pela tela da
 * recarga COM uma recarga viva já provou presença: o carro dele está plugado
 * na estação. Exigir que escaneie de novo só atrapalhava — em 2026-09-16 uma
 * cliente recarregando ficou 7 min entre a oferta e o scanner sem nunca
 * disparar o start (docs/bugs/2026-09-16-aspirador-scan-loop-sem-start.md).
 * Decisão Saulo 2026-09-16: dispensa SÓ nesse caso; adesivo, recibo, push e
 * estação seguem exigindo o scan. `origin` é só um parâmetro de URL, por isso
 * a recarga viva é conferida no servidor (`GET /charging/active`), não
 * presumida.
 */
export function shouldSkipScan(
  origin: VacuumOrigin | undefined,
  chargingLive: boolean
): boolean {
  return origin === 'charging' && chargingLive;
}

/** Texto do CTA: sem scan o botão promete o que faz ("Iniciar"), não um passo
 *  que não vai acontecer. */
export function startCtaLabel(opts: {
  skipScan: boolean;
  courtesyMode: boolean;
  courtesyCovers: boolean;
  priceLabel: string;
}): string {
  const verb = opts.skipScan ? 'Iniciar' : 'Escanear e iniciar';
  if (opts.courtesyMode) return verb;
  return `${verb} · ${opts.courtesyCovers ? 'grátis' : opts.priceLabel}`;
}

function minutesLabel(seconds: number): string {
  return `${Math.round(seconds / 60)}`;
}

/** Negativo como "− R$ 23,40" (minus tipográfico — identidade T8d). */
function formatSigned(value: number): string {
  return value < 0 ? `− ${formatBRL(Math.abs(value))}` : formatBRL(value);
}

/**
 * Tela de oferta do aspirador — redesign "Bow Carteiras" (T9Aspirador):
 * PackTiles de tempo + seletor de carteira (Saldo | Cashback) em radio-rows.
 * Cashback paga o aspirador MESMO com saldo negativo (matriz T8d) — por isso
 * `canPay` olha a carteira SELECIONADA, não só o saldo. A direção "outro
 * tempo" por minuto do design NÃO entra — o backend só aceita `block_seconds`
 * que bate com um `block_options` cadastrado no admin
 * (`amenities.service._resolve_block`).
 *
 * Fase de PREPARO (T9c′, frontend-only): depois do QR validar, tela navy com
 * anel âmbar 15→0 antes de chamar `startVacuum` — "Cancelar" aborta sem
 * cobrar nada (o start ainda não foi chamado).
 */
export default function VacuumOffer() {
  const { stationId } = useParams();
  // Atribuição do funil: de qual superfície o cliente chegou aqui.
  // Fica estável no primeiro render — a compra acontece depois do
  // scanner, e a URL não muda no meio do fluxo.
  const origin = parseVacuumOrigin(useLocation().search);
  const navigate = useNavigate();

  const [devices, setDevices] = useState<AmenityDeviceOut[] | null>(null);
  const [wallet, setWallet] = useState<WalletOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedSeconds, setSelectedSeconds] = useState<number | null>(null);
  const [scanning, setScanning] = useState(false);
  // Escape da tela de cortesia: cliente pediu pra ver os pacotes pagos.
  const [verPacotes, setVerPacotes] = useState(false);
  const [payFrom, setPayFrom] = useState<PayFrom>('cash');
  // Recarga viva confirmada pelo servidor — só é consultada quando a origem é
  // a tela da recarga (ver shouldSkipScan).
  const [chargingLive, setChargingLive] = useState(false);
  const [phase, setPhase] = useState<'offer' | 'prep'>('offer');
  const [prepLeft, setPrepLeft] = useState(PREP_SECONDS);
  const startingRef = useRef(false);
  const dev = useDevHardwareToggle();

  useEffect(() => {
    if (!stationId) return;
    let cancelled = false;
    (async () => {
      try {
        const [deviceList, walletData, active, chargingSession] = await Promise.all([
          amenitiesApi.devices(stationId),
          walletApi.get(),
          amenitiesApi.activeSession(),
          // Só quem veio da tela da recarga pode dispensar o scan — e só se a
          // recarga ainda está viva. Falha aqui vira "exige scan"; nunca
          // bloqueia a oferta.
          origin === 'charging'
            ? chargingApi.active().catch(() => null)
            : Promise.resolve(null)
        ]);
        if (cancelled) return;
        if (active) {
          navigate(`/amenities/sessions/${active.id}`, { replace: true });
          return;
        }
        setDevices(deviceList);
        setWallet(walletData);
        setChargingLive(chargingSession != null);
        const first = deviceList.find((d) => d.is_available) ?? deviceList[0];
        if (first && first.blocks.length > 0) {
          setSelectedSeconds(first.blocks[0].seconds);
        }
        // Métrica de funil — só depois de decidir que a oferta REALMENTE
        // aparece (não quando redirecionamos pra uma sessão em curso, que não
        // é uma visualização de oferta). Fire-and-forget: erro aqui não pode
        // encostar na tela. O remount do StrictMode é deduplicado no backend.
        if (deviceList.length > 0) {
          amenitiesApi.trackOfferView(stationId, origin).catch(() => {});
        }
      } catch (err) {
        if (!cancelled) setError(friendlyMessage(err, 'Falha ao carregar'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [stationId, navigate, origin]);

  // Tique da fase de preparo (só roda enquanto phase === 'prep').
  useEffect(() => {
    if (phase !== 'prep') return;
    const id = window.setInterval(() => {
      setPrepLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [phase]);

  // Zerou → liga. `startingRef` guarda contra double-fire com "Iniciar agora".
  useEffect(() => {
    if (phase === 'prep' && prepLeft <= 0 && !startingRef.current) {
      void doStart();
    }
    // doStart fecha sobre device/block/payFrom correntes — fora das deps de propósito.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, prepLeft]);

  if (error) {
    return (
      <div className="rounded-3xl bg-white p-5 text-sm text-bow-red shadow-soft">
        {error}
      </div>
    );
  }
  if (!devices || !wallet) {
    return <div className="h-48 animate-pulse rounded-3xl bg-white shadow-soft" />;
  }
  if (devices.length === 0) {
    return (
      <WarningCard
        title="Aspiração indisponível"
        message="Esta estação não tem aspirador ativo no momento."
      />
    );
  }

  // Fase 1: 1 aspirador por estação no caso típico. Pega o disponível (ou o
  // primeiro pra mostrar "em uso").
  const device = devices.find((d) => d.is_available) ?? devices[0];
  const blockEscolhido: AmenityBlock | undefined =
    device.blocks.find((b) => b.seconds === selectedSeconds) ?? device.blocks[0];
  // Cortesia (migration 054): cobre só até `courtesy_max_seconds`. Pacote
  // maior o cliente paga CHEIO — e o preço tem que refletir isso na hora,
  // senão a tela promete grátis e o backend cobra. O servidor é a autoridade;
  // isto aqui só espelha a mesma regra pra tela não mentir.
  const courtesyMax =
    device.courtesy_available === true ? (device.courtesy_max_seconds ?? 0) : 0;
  // Maior bloco que ainda cabe no teto — entrega o máximo de valor sem passar
  // do limite que o operador definiu na regra.
  const courtesyBlock =
    courtesyMax > 0
      ? [...device.blocks]
          .filter((b) => b.seconds <= courtesyMax)
          .sort((a, b) => b.seconds - a.seconds)[0]
      : undefined;
  // Tela DEDICADA da cortesia: some a grade de pacotes e o seletor de
  // carteira. Motivo: a tela antiga se contradizia — o tile dizia R$ 5,00, o
  // botão dizia grátis e ainda perguntava "pagar com". Com 9s de mediana até
  // a desistência (análise de 2026-08-22), ambiguidade nesse ponto custa a
  // conversão que a cortesia existe pra comprar.
  const courtesyMode = courtesyBlock != null && !verPacotes;
  const block: AmenityBlock | undefined = courtesyMode ? courtesyBlock : blockEscolhido;
  const courtesyCovers =
    courtesyMax > 0 && block != null && block.seconds <= courtesyMax;
  const price = courtesyCovers ? 0 : block ? Number(block.price_brl) : 0;
  const { available, bonusAvailable } = walletNumbers(wallet);
  const bonusEmpty = bonusAvailable <= 0;
  // canPay considera a carteira SELECIONADA (T9c): 0 de saldo e 20 de
  // cashback precisa conseguir ligar.
  // Se a carteira marcada não cobre e a outra cobre, a seleção CAI pra outra
  // (mesmo padrão do payFromEffective do ConfigureCharge). Sem isso o cliente
  // em pendência abre a tela no default 'cash', vê o saldo negativo e o CTA
  // desabilitado — apesar de o cashback dele pagar o bloco (matriz T8d).
  const payFromEffective: PayFrom =
    (payFrom === 'cash' ? available : bonusAvailable) >= price
      ? payFrom
      : (payFrom === 'cash' ? bonusAvailable : available) >= price
        ? payFrom === 'cash'
          ? 'bonus'
          : 'cash'
        : payFrom;
  // Cortesia não passa por gate de carteira NENHUM — grátis não gera dívida,
  // e é justamente quem está sem saldo (ou devendo) que ela existe pra
  // alcançar. Espelha o `start_vacuum`, que pula o gate quando o preço é 0.
  const canPaySelected =
    courtesyCovers ||
    (payFromEffective === 'cash' ? available : bonusAvailable) >= price;
  const canPayAny =
    courtesyCovers || available >= price || bonusAvailable >= price;
  const expectedScanPath = `/amenities/${stationId}/vacuum`;
  const skipScan = shouldSkipScan(origin, chargingLive);

  /** QR validado (ou dispensado) → fase de PREPARO, não o start direto (T9c′). */
  function beginPrep() {
    setSubmitError(null);
    setPrepLeft(PREP_SECONDS);
    setPhase('prep');
  }

  async function doStart() {
    if (!device || !block || startingRef.current) return;
    startingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const session = await amenitiesApi.startVacuum({
        device_id: device.id,
        block_seconds: block.seconds,
        pay_from: payFromEffective,
        origin
      });
      navigate(`/amenities/sessions/${session.id}`);
    } catch (err) {
      startingRef.current = false;
      setSubmitting(false);
      setPhase('offer');
      setPrepLeft(PREP_SECONDS);
      setSubmitError(friendlyMessage(err, 'Falha ao ligar o aspirador'));
    }
  }

  function cancelPrep() {
    if (startingRef.current) return;
    // Nada foi cobrado — o start ainda não foi chamado.
    setPhase('offer');
    setPrepLeft(PREP_SECONDS);
  }

  // NSS (nss-amenities.jsx · VacuumOfferNS): mesmo frame do ConfigureCharge —
  // viewport fixa (100dvh − nav), miolo rolável só por acessibilidade,
  // pill "desliga automaticamente" + CTA ancorados no rodapé.
  return (
    <div
      className="-mx-4 -mb-24 flex flex-col overflow-hidden"
      style={{
        marginTop: 'calc(-1 * max(1rem, env(safe-area-inset-top)))',
        height:
          'calc(100dvh - 62px - max(0.75rem, env(safe-area-inset-bottom)))'
      }}
    >
      {phase === 'prep' ? (
        /* Fase PREPARO (T9c′) — substitui o conteúdo mantendo o frame. */
        <div
          className="flex min-h-0 flex-1 flex-col items-center justify-center gap-7 bg-bow-royal px-6 text-bow-cream [@media(max-height:740px)]:gap-5"
          style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
        >
          <span
            className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-bow-cream"
            style={{ background: 'rgba(241,237,228,0.14)' }}
          >
            <span className="h-2 w-2 animate-pulse rounded-full bg-bow-yellow" />
            Preparando
          </span>
          <ProgressRing
            size={250}
            strokeWidth={10}
            fraction={prepLeft / PREP_SECONDS}
            color="#FFB92A"
            track="rgba(241,237,228,0.12)"
          >
            <span
              className="tabular-nums"
              style={{ fontSize: 84, fontWeight: 600, lineHeight: 1 }}
            >
              {prepLeft}
            </span>
            <span className="mt-1 text-sm text-bow-cream/60">segundos</span>
          </ProgressRing>
          <div className="flex w-full max-w-[300px] flex-col gap-2">
            <button
              type="button"
              onClick={() => void doStart()}
              disabled={submitting}
              className="inline-flex h-[54px] items-center justify-center rounded-full border-[1.5px] border-bow-cream/35 px-6 text-base font-semibold text-bow-cream transition hover:bg-bow-cream/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Ligando…' : 'Iniciar agora'}
            </button>
            <button
              type="button"
              onClick={cancelPrep}
              disabled={submitting}
              className="inline-flex h-11 items-center justify-center rounded-full px-6 text-sm font-semibold text-bow-cream/70 transition hover:bg-bow-cream/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div
          className="flex min-h-0 flex-1 flex-col px-5"
          style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
        >
          {/* Header com voltar contextual (mock NSS) */}
          <div className="flex flex-shrink-0 items-center gap-2.5 pb-2 pt-1">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-bow-royal/5 text-bow-royal"
              aria-label="Voltar"
            >
              <ChevronLeftIcon />
            </button>
            <div>
              <Eyebrow>Comodidade</Eyebrow>
              <Display
                size={26}
                className="mt-0.5 [@media(max-height:740px)]:!text-[23px]"
              >
                Aspiração
              </Display>
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center justify-between gap-3 pb-3 [@media(max-height:740px)]:pb-2">
            <p className="text-sm text-bow-royal/65">{device.name}</p>
            <StatusPill tone={device.is_available ? 'green' : 'muted'}>
              {device.is_available ? 'Disponível' : 'Em uso'}
            </StatusPill>
          </div>

          {/* Miolo — rola só com fonte de acessibilidade grande (§5.2) */}
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto [@media(max-height:740px)]:gap-3">
            {courtesyMode ? (
              <CourtesyPanel
                seconds={courtesyBlock!.seconds}
                nextBlock={device.blocks.find((b) => b.seconds > courtesyMax)}
                onVerPacotes={() => {
                  setVerPacotes(true);
                  setSelectedSeconds(
                    device.blocks.find((b) => b.seconds > courtesyMax)?.seconds ??
                      courtesyBlock!.seconds
                  );
                }}
              />
            ) : (
              <>
            {/* Pacotes — tiles-herói (B · Pacotes) */}
            <div className="flex-shrink-0">
              <Eyebrow>Escolha o tempo</Eyebrow>
              <div className="mt-2.5 grid grid-cols-2 gap-2.5 [@media(max-height:740px)]:mt-2">
                {device.blocks.map((b) => (
                  <PackTile
                    key={b.seconds}
                    block={b}
                    selected={b.seconds === block?.seconds}
                    free={courtesyMax > 0 && b.seconds <= courtesyMax}
                    onSelect={() => setSelectedSeconds(b.seconds)}
                  />
                ))}
              </div>
            </div>

            {/* Pagar com — some quando a cortesia cobre o bloco escolhido:
                perguntar a carteira de algo que não será debitado foi
                exatamente a contradição que motivou o redesign. */}
            {!courtesyCovers && (
            <>
            {/* radio-rows Saldo | Cashback (T9) */}
            <div className="flex-shrink-0">
              <Eyebrow>Pagar com</Eyebrow>
              <div className="mt-2.5 flex flex-col gap-2 [@media(max-height:740px)]:mt-2">
                <button
                  type="button"
                  onClick={() => setPayFrom('cash')}
                  aria-pressed={payFromEffective === 'cash'}
                  className={`flex w-full items-center gap-3 rounded-[18px] border-[1.5px] bg-white px-3.5 py-3 text-left transition ${
                    payFromEffective === 'cash' ? 'border-bow-royal' : 'border-bow-royal/10'
                  }`}
                >
                  <span className="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-[13px] bg-bow-royal/[0.06] text-bow-royal">
                    <WalletIcon />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="text-[13px] leading-tight text-bow-royal/55">
                      Saldo
                    </span>
                    <span
                      className={available < 0 ? 'text-bow-red' : 'text-bow-royal'}
                      style={{
                        fontFamily: 'Chillax, system-ui, sans-serif',
                        fontWeight: 600,
                        fontSize: 17,
                        letterSpacing: '-0.02em'
                      }}
                    >
                      {formatSigned(available)}
                    </span>
                  </span>
                  <Radio checked={payFromEffective === 'cash'} tint="#001B74" />
                </button>

                {/* Cashback zerado → apagado e não-selecionável (T9c) */}
                <button
                  type="button"
                  onClick={() => {
                    if (!bonusEmpty) setPayFrom('bonus');
                  }}
                  disabled={bonusEmpty}
                  aria-pressed={payFromEffective === 'bonus'}
                  className={`flex w-full items-center gap-3 rounded-[18px] border-[1.5px] bg-white px-3.5 py-3 text-left transition ${
                    payFromEffective === 'bonus' ? 'border-[#A23C92]' : 'border-bow-royal/10'
                  } ${bonusEmpty ? 'cursor-default opacity-[0.55]' : ''}`}
                >
                  <CashbackBadge size={38} />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span
                      className="text-[13px] leading-tight"
                      style={{ color: '#A23C92' }}
                    >
                      Cashback
                    </span>
                    <span
                      style={{
                        color: '#A23C92',
                        fontFamily: 'Chillax, system-ui, sans-serif',
                        fontWeight: 600,
                        fontSize: 17,
                        letterSpacing: '-0.02em'
                      }}
                    >
                      {formatBRL(bonusAvailable)}
                    </span>
                  </span>
                  <Radio
                    checked={payFromEffective === 'bonus' && !bonusEmpty}
                    tint="#A23C92"
                  />
                </button>
              </div>
            </div>
                </>
            )}
              </>
            )}
          </div>

          {/* Rodapé ancorado: pill + CTA nunca saem da tela (NSS). Card de
              dev aqui — no miolo rolável ele sumia abaixo da dobra. */}
          <div className="flex flex-shrink-0 flex-col gap-2.5 pb-2.5 pt-2.5 [@media(max-height:740px)]:gap-2 [@media(max-height:740px)]:pt-2">
            {dev.isDev && (
              <Card className="border-2 border-dashed border-bow-yellow/40 bg-bow-yellow/5 p-3">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={dev.useHardware}
                    onChange={(e) => dev.setUseHardware(e.target.checked)}
                    className="mt-0.5 h-5 w-5 cursor-pointer accent-bow-royal"
                  />
                  <span className="flex flex-col text-[13px]">
                    <span className="font-semibold text-bow-royal">
                      Usar hardware real ({dev.useHardware ? 'ON' : 'OFF'})
                    </span>
                    <span className="text-xs text-bow-royal/65">
                      {dev.useHardware
                        ? '⚠️ Ao ligar, o relé do aspirador físico VAI ACIONAR.'
                        : 'Mock — o relé não é acionado, fluxo só na UI.'}
                    </span>
                  </span>
                </label>
              </Card>
            )}

            {submitError && (
              <p className="px-1 text-center text-sm text-bow-red">
                {submitError}
              </p>
            )}

            <div className="flex justify-center">
              <span
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[12.5px] font-semibold text-bow-royal/70"
                style={{ background: 'rgba(0,27,116,0.045)', borderRadius: 14 }}
              >
                <TimerIcon />
                Desliga automaticamente
              </span>
            </div>

            {!canPayAny ? (
              <Link
                to="/wallet/recharge"
                state={{ from: `/amenities/${stationId}/vacuum` }}
                className="inline-flex h-12 items-center justify-center rounded-full bg-bow-royal px-6 text-sm font-semibold text-bow-cream"
              >
                Adicionar crédito
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => (skipScan ? beginPrep() : setScanning(true))}
                disabled={
                  submitting || !device.is_available || !block || !canPaySelected
                }
                className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-bow-royal px-6 text-sm font-semibold text-bow-cream disabled:cursor-not-allowed disabled:opacity-50"
              >
                {!device.is_available ? (
                  'Em uso'
                ) : (
                  <>
                    {!skipScan && <QrIcon />}
                    {startCtaLabel({
                      skipScan,
                      courtesyMode,
                      courtesyCovers,
                      priceLabel: formatBRL(price)
                    })}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Início só dispara ao escanear o QR físico do aspirador — evita
          acionar o relé sem o cliente estar de fato na frente do equipamento
          (feedback Saulo 2026-07-08). Exceção única: recarga viva vinda da
          tela da recarga (shouldSkipScan). O QR validado abre a fase de
          PREPARO, não o start direto (T9c′). */}
      {scanning && (
        <ScanQrOverlay
          onClose={() => setScanning(false)}
          onResolved={(path) => {
            setScanning(false);
            if (isSameVacuumTarget(path, expectedScanPath)) {
              beginPrep();
            } else {
              setSubmitError(
                'Esse QR não é deste aspirador. Escaneie o código no equipamento.'
              );
            }
          }}
        />
      )}
    </div>
  );
}

function PackTile({
  block,
  selected,
  onSelect,
  free = false
}: {
  block: AmenityBlock;
  selected: boolean;
  onSelect: () => void;
  /** Bloco coberto pela cortesia — mostra GRÁTIS no lugar do preço. */
  free?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex flex-col items-start rounded-2xl border-[1.5px] p-4 text-left transition ${
        selected ? 'border-bow-royal bg-bow-royal/[0.04]' : 'border-bow-royal/10 bg-white'
      }`}
    >
      <span
        className={`flex h-9 w-9 items-center justify-center rounded-xl ${
          selected ? 'bg-bow-royal text-bow-cream' : 'bg-bow-royal/6 text-bow-royal'
        }`}
      >
        <VacuumIcon size={18} />
      </span>
      <span className="mt-3 text-[26px] font-semibold leading-none tracking-tight text-bow-royal">
        {minutesLabel(block.seconds)}
        <span className="ml-1 text-sm font-medium text-bow-royal/55">min</span>
      </span>
      <span className="mt-2 flex w-full items-baseline gap-1.5 border-t border-bow-royal/8 pt-2 text-lg font-semibold tracking-tight text-bow-royal">
        {free ? (
          <>
            <span style={{ color: '#00A664' }}>Grátis</span>
            <span className="text-[13px] font-medium text-bow-royal/40 line-through">
              {formatBRL(block.price_brl)}
            </span>
          </>
        ) : (
          formatBRL(block.price_brl)
        )}
      </span>
    </button>
  );
}

/** Anel de progresso SVG (preparo T9c′). `fraction` 0..1 = quanto resta. */
function ProgressRing({
  size,
  strokeWidth,
  fraction,
  color,
  track,
  children
}: {
  size: number;
  strokeWidth: number;
  fraction: number;
  color: string;
  track: string;
  children: React.ReactNode;
}) {
  const r = (size - strokeWidth) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(1, fraction));
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={track}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - clamped)}
          style={{ transition: 'stroke-dashoffset 1s linear' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {children}
      </div>
    </div>
  );
}

/** Badge circular `$` da carteira Cashback (identidade Bow Carteiras). */
function CashbackBadge({ size = 38 }: { size?: number }) {
  return (
    <span
      aria-hidden
      className="flex flex-shrink-0 items-center justify-center rounded-full text-white"
      style={{
        width: size,
        height: size,
        background: 'linear-gradient(135deg,#835DD0,#C04CAE)',
        fontFamily: 'Chillax, system-ui, sans-serif',
        fontWeight: 700,
        fontSize: size * 0.5,
        lineHeight: 1
      }}
    >
      $
    </span>
  );
}

/** Radio visual das rows de carteira. */
function Radio({ checked, tint }: { checked: boolean; tint: string }) {
  return (
    <span
      aria-hidden
      className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-[1.5px]"
      style={{ borderColor: checked ? tint : 'rgba(0,27,116,0.25)' }}
    >
      {checked && (
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ background: tint }}
        />
      )}
    </span>
  );
}

function WalletIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
      <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
      <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
    </svg>
  );
}

function TimerIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={14}
      height={14}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="10" x2="14" y1="2" y2="2" />
      <line x1="12" x2="15" y1="14" y2="11" />
      <circle cx="12" cy="14" r="8" />
    </svg>
  );
}

function VacuumIcon({ size = 22, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >      <path d="M9.6 4.6A2 2 0 1 1 11 8H2" />
      <path d="M17.7 5.7A2.5 2.5 0 1 1 19.5 10H2" />
      <path d="M12.6 19.4A2 2 0 1 0 14 16H2" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m15 6-6 6 6 6" />
    </svg>
  );
}

function QrIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3 8V5a2 2 0 0 1 2-2h3" />
      <path d="M16 3h3a2 2 0 0 1 2 2v3" />
      <path d="M21 16v3a2 2 0 0 1-2 2h-3" />
      <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
      <rect x="8" y="8" width="3.2" height="3.2" rx="0.6" />
      <rect x="13" y="8" width="3.2" height="3.2" rx="0.6" />
      <rect x="8" y="13" width="3.2" height="3.2" rx="0.6" />
      <path d="M13 14.6h3.2M14.6 13v3.2" />
    </svg>
  );
}

/**
 * Painel da CORTESIA — substitui a grade de pacotes e o seletor de carteira
 * quando o cliente tem a aspiração grátis.
 *
 * Existe porque a tela compartilhada se contradizia: o tile do bloco coberto
 * mostrava "R$ 5,00", o botão dizia "grátis" e a seção "Pagar com" pedia uma
 * carteira que não ia ser debitada. Com mediana de 9 s até a desistência
 * (análise de abandono de 2026-08-22), ambiguidade aqui custa exatamente a
 * conversão que a cortesia foi criada pra comprar.
 *
 * Uma decisão só: escanear. O escape pros pacotes pagos fica discreto embaixo
 * — quem quer mais tempo acha, quem só quer o grátis não tropeça.
 */
function CourtesyPanel({
  seconds,
  nextBlock,
  onVerPacotes
}: {
  seconds: number;
  /** Menor bloco NÃO coberto — ancora o "quero mais tempo". */
  nextBlock?: AmenityBlock;
  onVerPacotes: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center [@media(max-height:740px)]:gap-3">
      <span
        className="flex h-[76px] w-[76px] items-center justify-center rounded-full [@media(max-height:740px)]:h-16 [@media(max-height:740px)]:w-16"
        style={{
          background: 'linear-gradient(135deg, #00C072, #00BBDE)',
          boxShadow: '0 12px 30px rgba(0,192,114,0.32)'
        }}
      >
        <span className="flex h-[58px] w-[58px] items-center justify-center rounded-full bg-bow-cream [@media(max-height:740px)]:h-12 [@media(max-height:740px)]:w-12">
          <img
            src="/logo/aspirador-bow.png"
            alt=""
            className="h-10 w-10 object-contain [@media(max-height:740px)]:h-8 [@media(max-height:740px)]:w-8"
          />
        </span>
      </span>

      <div>
        <Display
          size={26}
          className="[@media(max-height:740px)]:!text-[22px]"
        >
          Sua primeira aspiração
          <br />é por nossa conta
        </Display>
      </div>

      <div
        className="inline-flex items-baseline gap-2 rounded-[18px] px-5 py-3"
        style={{ background: 'rgba(0,192,114,0.10)' }}
      >
        <span
          className="text-bow-royal"
          style={{
            fontFamily: 'Chillax, system-ui, sans-serif',
            fontWeight: 600,
            fontSize: 22,
            letterSpacing: '-0.02em'
          }}
        >
          {minutesLabel(seconds)} min
        </span>
        <span className="text-bow-royal/40">·</span>
        <span
          style={{
            color: '#00A664',
            fontFamily: 'Chillax, system-ui, sans-serif',
            fontWeight: 600,
            fontSize: 22,
            letterSpacing: '-0.02em'
          }}
        >
          grátis
        </span>
      </div>

      {nextBlock && (
        <button
          type="button"
          onClick={onVerPacotes}
          className="text-[13.5px] font-semibold text-bow-royal/60 underline-offset-4 hover:underline"
        >
          Quero mais tempo ({minutesLabel(nextBlock.seconds)} min ·{' '}
          {formatBRL(Number(nextBlock.price_brl))}) ›
        </button>
      )}
    </div>
  );
}

function WarningCard({ title, message }: { title: string; message: string }) {
  return (
    <Card className="flex flex-col gap-2 p-5">
      <span className="font-semibold">{title}</span>
      <span className="text-sm text-bow-royal/65">{message}</span>
    </Card>
  );
}
